import {
  AddFairCandidatesRequestDto,
  AddFairCandidatesResponseDto,
  FairCandidatesResponseDto,
  FairResultsResponseDto,
  UpdateFairCandidateRequestDto,
} from '@/api/generated';
import { useApiClient } from '@/api/util/useApiClient.util.ts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const FAIR_RESULTS_KEY = 'fair-results';
const FAIR_CANDIDATES_KEY = 'fair-candidates';

export const useGetFairResults = (adminUserId: string) => {
  const { adminCandidatesApi } = useApiClient();

  const query = useQuery<FairResultsResponseDto>({
    enabled: !!adminUserId,
    queryFn: async () => (await adminCandidatesApi.getFairResults(adminUserId)).data,
    queryKey: [FAIR_RESULTS_KEY, adminUserId],
  });

  return { isLoading: query.isLoading, results: query.data?.items };
};

interface FairCandidatesParams {
  adminUserId: string;
  /** Only the candidates of this fair; all of them without it */
  note?: string;
  page: number;
  pageSize: number;
}

export const useGetFairCandidates = ({
  adminUserId,
  note,
  page,
  pageSize,
}: FairCandidatesParams) => {
  const { adminCandidatesApi } = useApiClient();

  const query = useQuery<FairCandidatesResponseDto>({
    enabled: !!adminUserId,
    placeholderData: keepPreviousData,
    queryFn: async () =>
      (await adminCandidatesApi.getFairCandidates(page, pageSize, adminUserId, note)).data,
    queryKey: [FAIR_CANDIDATES_KEY, adminUserId, note, page, pageSize],
  });

  return { fairCandidates: query.data, isFetching: query.isFetching, isLoading: query.isLoading };
};

/**
 * Refreshes the results and the list, which change whenever a fair candidate is added or edited
 */
export const useRefreshFairs = () => {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: [FAIR_RESULTS_KEY] }),
      queryClient.invalidateQueries({ queryKey: [FAIR_CANDIDATES_KEY] }),
    ]);
};

export const useAddFairCandidates = (adminUserId: string) => {
  const { adminCandidatesApi } = useApiClient();
  const refresh = useRefreshFairs();

  const mutation = useMutation<AddFairCandidatesResponseDto, Error, AddFairCandidatesRequestDto>({
    mutationFn: async dto => (await adminCandidatesApi.addFairCandidates(adminUserId, dto)).data,
    onSuccess: () => refresh(),
  });

  return {
    addFairCandidates: mutation.mutateAsync,
    isAdding: mutation.isPending,
    reset: mutation.reset,
  };
};

interface UpdateParams {
  dto: UpdateFairCandidateRequestDto;
  email: string;
}

export const useUpdateFairCandidate = (adminUserId: string) => {
  const { adminCandidatesApi } = useApiClient();
  const refresh = useRefreshFairs();

  const mutation = useMutation<void, Error, UpdateParams>({
    mutationFn: async ({ dto, email }) => {
      await adminCandidatesApi.updateFairCandidate(adminUserId, email, dto);
    },
    onSuccess: () => refresh(),
  });

  return { isUpdating: mutation.isPending, updateFairCandidate: mutation.mutateAsync };
};
